// The scope a person starts at when they can only mean one (UX-006, the
// 2026-11-01 review). A person whose roles are all at one hotel opened on
// "All sites", and every page they came for read "Choose a site." on every
// visit. Where the estate they can see holds exactly one site, or exactly one
// customer, that is where they start.
//
// The spec is silent on a default scope (ZENITH.md §18 names the switcher and
// the remembered pick, and nothing else); this is the assumption, named:
//
// - **Only on a fresh start**: an address that named no scope, in a browser
//   that remembered none. A remembered "All sites" is a pick and is kept, and
//   a link that carries a scope is followed as it always was.
// - **Only for a person every one of whose memberships is at a customer or a
//   site** — ADR-0489's test for a person who works inside one customer's
//   estate. An organization's people and a superuser keep "All sites": their
//   Admin pages follow the scope, and narrowing them by default would hide
//   people they manage (UX-005's third part, which is an ADR's to rule).
// - **Never past the drain's bound** (#188): a cut list cannot say there is
//   only one.
//
// It is a pick made for the person and nothing more: the address carries it,
// the switcher shows it and "All sites" is one click away.
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import type { components } from "../api/schema";
import { useEstate, type Lists } from "./estate";
import { useScope, type Scope } from "./scope";
import { useSession } from "./session";

type Me = components["schemas"]["Me"];

/** The one site, else the one customer, this person can see, where they work
 * inside one customer's estate; undefined otherwise. */
export function onlyScope(me: Me | undefined, l: Lists | undefined): Scope | undefined {
  if (!me || !l || l.more || me.user.superuser || me.break_glass) return undefined;
  const ms = me.memberships;
  if (ms.length === 0 || !ms.every((m) => m.scope_type === "customer" || m.scope_type === "site")) return undefined;
  if (l.sites.length === 1) {
    const s = l.sites[0]!;
    return { orgId: s.org_id, customerId: s.customer_id, siteId: s.id };
  }
  const customers = new Set([...l.sites.map((s) => s.customer_id), ...l.customers.map((c) => c.id)]);
  if (customers.size !== 1) return undefined;
  const c = l.customers[0];
  const orgId = c?.org_id ?? l.sites[0]?.org_id;
  const customerId = [...customers][0];
  return orgId && customerId ? { orgId, customerId } : undefined;
}

/** Moves a fresh start to `onlyScope`'s answer, once, as a replace: the back
 * button does not return to an "All sites" the person never chose. */
export function useOnlyScopeByDefault(): void {
  const { me } = useSession();
  const { lists } = useEstate();
  const { fresh, pickTarget } = useScope();
  const navigate = useNavigate();
  const ended = useRef(false);
  useEffect(() => {
    if (ended.current) return;
    if (!fresh) { ended.current = true; return; }
    if (!me || !lists) return;
    ended.current = true;
    const to = onlyScope(me, lists);
    if (to) navigate(pickTarget(to), { replace: true });
  }, [fresh, me, lists, pickTarget, navigate]);
}
